import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { getAdSessionId, recordImpression, getImpressionCount, getFrequencyCap } from '@/lib/adSession';
import { detectVisitorCity, getCachedVisitorCity } from '@/lib/visitorCity';

interface Campaign {
  id: string;
  title: string;
  description: string | null;
  image_url: string;
  target_url: string | null;
  advertiser_name: string;
  tier: string | null;
  start_date: string;
  end_date: string;
  status: string | null;
  is_active: boolean;
  pages: any;
  placement: string | null;
  placement_id: string | null;
  target_cities: string[] | null;
}

interface PlacementMeta {
  id: string;
  code: string;
  name: string;
  width_px: number | null;
  height_px: number | null;
  available_spots: number;
}

export interface BannerSlotProps {
  /** Pass either the placement id OR the placement code (e.g. "site_wide_right"). */
  placementId?: string;
  placementCode?: string;
  /** Page slug (matches banner_placements.pages array, e.g. "homepage", "buy"). */
  page: string;
  /** Override city (preview); otherwise uses cached/detected visitor city. */
  cityOverride?: string;
  /** Override "today" for previewing future/past schedules. */
  dateOverride?: string;
  /** Skip event logging + frequency cap (admin preview). */
  previewMode?: boolean;
  /** Limit how many slots to render (defaults to placement.available_spots). */
  maxSpots?: number;
  className?: string;
  /** Render layout. */
  layout?: 'stack' | 'row';
  /** Auto-rotate through eligible campaigns when more exist than visible spots. Default true. */
  rotate?: boolean;
  /** Rotation interval in ms. Default 7000. */
  rotationIntervalMs?: number;
}

const trackedImpressions = new Set<string>();
const trackedClicks = new Set<string>();

export function BannerSlot({
  placementId,
  placementCode,
  page,
  cityOverride,
  dateOverride,
  previewMode = false,
  maxSpots,
  className,
  layout = 'stack',
  rotate = true,
  rotationIntervalMs = 7000,
}: BannerSlotProps) {
  const sessionId = useMemo(() => getAdSessionId(), []);
  const today = dateOverride || new Date().toISOString().slice(0, 10);

  // Resolve placement metadata
  const { data: placement } = useQuery({
    queryKey: ['banner-placement', placementId, placementCode],
    enabled: !!(placementId || placementCode),
    queryFn: async () => {
      let q = supabase.from('banner_placements' as any).select('id,code,name,width_px,height_px,available_spots').eq('is_active', true);
      if (placementId) q = q.eq('id', placementId);
      else if (placementCode) q = q.eq('code', placementCode);
      const { data, error } = await q.maybeSingle();
      if (error) throw error;
      return (data as unknown) as PlacementMeta | null;
    },
    // Placements rarely change → cache 5 min, but revalidate on focus so admin edits show up quickly.
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  // Detect city in the background (only for live mode)
  const [detectedCity, setDetectedCity] = useState<string>(getCachedVisitorCity());
  useEffect(() => {
    if (previewMode) return;
    if (detectedCity) return;
    let alive = true;
    detectVisitorCity().then((c) => { if (alive) setDetectedCity(c); });
    return () => { alive = false; };
  }, [previewMode, detectedCity]);

  const effectiveCity = (cityOverride ?? detectedCity ?? '').trim();

  const { data: campaigns, isLoading } = useQuery({
    queryKey: ['banner-slot', placement?.id, page, today, effectiveCity, previewMode],
    enabled: !!placement?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ad_campaigns')
        .select('id,title,description,image_url,target_url,advertiser_name,tier,start_date,end_date,status,is_active,pages,placement,placement_id,target_cities')
        .eq('placement_id', placement!.id)
        .lte('start_date', today)
        .gte('end_date', today)
        .limit(50);
      if (error) throw error;
      return (data || []) as Campaign[];
    },
    // Schedule/status changes should appear within ~90s; window focus triggers a quick revalidation.
    staleTime: 90 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: previewMode ? false : 5 * 60 * 1000,
  });

  // Tier weights for fair-rotation scoring (higher tier still wins ties, but rotation breaks ties).
  const tierWeight = (t: string | null | undefined) => {
    const v = (t || '').toLowerCase();
    if (v === 'super_hot' || v === 'premium') return 3;
    if (v === 'hot') return 2;
    if (v === 'standard') return 1;
    return 0;
  };

  // Step 1: figure out which campaigns are eligible by targeting, and which got cut by the frequency cap.
  const cap = Math.max(1, maxSpots ?? placement?.available_spots ?? 1);
  const frequencyCap = getFrequencyCap();

  const { eligible, cappedOut } = useMemo(() => {
    const eligible: Campaign[] = [];
    const cappedOut: Array<{ campaign: Campaign; impressions: number; reason: string }> = [];
    (campaigns || []).forEach((c) => {
      const status = (c.status || (c.is_active ? 'active' : 'paused')).toLowerCase();
      if (!previewMode) {
        if (!c.is_active) return;
        if (status && status !== 'active') return;
      } else {
        if (status === 'completed' || status === 'archived' || status === 'expired') return;
      }
      const pages = Array.isArray(c.pages) ? c.pages : [];
      if (pages.length && !pages.includes(page)) return;
      const cities = (c.target_cities || []).map((x) => (x || '').trim()).filter(Boolean);
      if (cities.length && effectiveCity && !cities.includes(effectiveCity)) return;
      if (cities.length && !effectiveCity && !previewMode) return;

      // Frequency-cap check (per campaign × placement, per session window)
      if (placement?.code) {
        const seen = getImpressionCount(c.id, placement.code);
        if (seen >= frequencyCap) {
          cappedOut.push({
            campaign: c,
            impressions: seen,
            reason: `Hit cap of ${frequencyCap} impressions in this session`,
          });
          if (!previewMode) return; // hide live; preview keeps them in the report only
          return;
        }
      }
      eligible.push(c);
    });
    return { eligible, cappedOut };
  }, [campaigns, page, effectiveCity, previewMode, placement?.code, frequencyCap]);

  // Step 2: fair rotation. Sort by (tierWeight desc, impressionsThisSession asc, jitter).
  // The jitter is stable per (sessionId, campaign) so reloads in the same session stay coherent
  // but different visitors / new sessions see a different ordering.
  const rotationSeed = sessionId + ':' + (placement?.code || '') + ':' + today;
  const sortedEligible = useMemo(() => {
    const hash = (s: string) => {
      let h = 2166136261;
      for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = (h * 16777619) >>> 0; }
      return h / 0xffffffff;
    };
    const scored = eligible.map((c) => ({
      c,
      weight: tierWeight(c.tier),
      impressions: placement?.code ? getImpressionCount(c.id, placement.code) : 0,
      jitter: hash(rotationSeed + ':' + c.id),
    }));
    scored.sort((a, b) => {
      if (b.weight !== a.weight) return b.weight - a.weight;
      if (a.impressions !== b.impressions) return a.impressions - b.impressions;
      return a.jitter - b.jitter;
    });
    return scored.map((s) => s.c);
  }, [eligible, placement?.code, rotationSeed]);

  // Auto-rotate when more eligible campaigns exist than visible spots.
  const [rotationOffset, setRotationOffset] = useState(0);
  const shouldRotate = rotate && !previewMode && sortedEligible.length > cap;
  useEffect(() => {
    if (!shouldRotate) return;
    const id = window.setInterval(() => {
      setRotationOffset((o) => (o + cap) % Math.max(1, sortedEligible.length));
    }, Math.max(2000, rotationIntervalMs));
    return () => window.clearInterval(id);
  }, [shouldRotate, sortedEligible.length, cap, rotationIntervalMs]);

  const visible = useMemo(() => {
    if (!sortedEligible.length) return [];
    const out: Campaign[] = [];
    for (let i = 0; i < cap && i < sortedEligible.length; i++) {
      out.push(sortedEligible[(rotationOffset + i) % sortedEligible.length]);
    }
    return out;
  }, [sortedEligible, cap, rotationOffset]);

  // Log impressions
  const idsKey = visible.map((c) => c.id).join(',');
  const lastTrackedKey = useRef<string>('');
  useEffect(() => {
    if (previewMode) return;
    if (!visible.length || !placement?.code) return;
    if (lastTrackedKey.current === idsKey) return;
    lastTrackedKey.current = idsKey;
    visible.forEach((c) => {
      if (trackedImpressions.has(c.id + '|' + placement.code)) return;
      trackedImpressions.add(c.id + '|' + placement.code);
      recordImpression(c.id, placement.code);
      supabase.rpc('log_ad_event', {
        _campaign_id: c.id,
        _event_type: 'impression',
        _session_id: sessionId,
        _placement: placement.code,
        _page: page,
        _user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        _referrer: typeof document !== 'undefined' ? document.referrer : null,
        _city: effectiveCity || null,
      } as any).then(() => {});
    });
  }, [idsKey, visible, sessionId, placement?.code, page, previewMode, effectiveCity]);

  const handleClick = (id: string) => {
    if (previewMode || !placement?.code) return;
    const k = id + '|click|' + placement.code;
    if (trackedClicks.has(k)) return;
    trackedClicks.add(k);
    supabase.rpc('log_ad_event', {
      _campaign_id: id,
      _event_type: 'click',
      _session_id: sessionId,
      _placement: placement.code,
      _page: page,
      _user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      _referrer: typeof document !== 'undefined' ? document.referrer : null,
      _city: effectiveCity || null,
    } as any).then(() => {});
  };

  if (!placement) {
    return previewMode ? (
      <div className={cn('rounded-md border border-dashed p-3 text-xs text-muted-foreground', className)}>
        Placement not found.
      </div>
    ) : null;
  }

  const aspectStyle = placement.width_px && placement.height_px
    ? { aspectRatio: `${placement.width_px} / ${placement.height_px}`, maxWidth: placement.width_px }
    : undefined;

  if (isLoading) {
    return (
      <div className={cn(layout === 'row' ? 'flex gap-3 flex-wrap' : 'space-y-3', className)}>
        <Skeleton className="w-full" style={aspectStyle as any} />
      </div>
    );
  }

  const renderOne = (c: Campaign) => {
    const inner = (
      <div
        className="relative rounded-md overflow-hidden border bg-card group hover:shadow-md transition-shadow w-full"
        style={aspectStyle}
      >
        <img
          src={c.image_url}
          alt={c.title}
          loading="lazy"
          className="w-full h-full object-cover"
        />
        <Badge className="absolute top-1.5 left-1.5 text-[9px] h-4 px-1.5 bg-background/90 text-foreground border-0">
          Ad
        </Badge>
        {previewMode && (
          <Badge variant="outline" className="absolute bottom-1.5 left-1.5 text-[9px] h-4 px-1.5 bg-background/90">
            {c.title}
          </Badge>
        )}
      </div>
    );
    return c.target_url && !previewMode ? (
      <a
        key={c.id}
        href={c.target_url}
        target="_blank"
        rel="noopener noreferrer sponsored"
        onClick={() => handleClick(c.id)}
        className="block"
      >
        {inner}
      </a>
    ) : (
      <div key={c.id}>{inner}</div>
    );
  };

  if (!visible.length && !(previewMode && cappedOut.length)) {
    return previewMode ? (
      <div className={cn('rounded-md border border-dashed p-3 text-xs text-muted-foreground', className)}>
        No banner matches this placement, page, city &amp; date.
      </div>
    ) : null;
  }

  return (
    <div className={cn('space-y-3', className)}>
      {visible.length > 0 && (
        <div className={cn(layout === 'row' ? 'flex gap-3 flex-wrap' : 'space-y-3')}>
          {visible.map(renderOne)}
        </div>
      )}

      {previewMode && cappedOut.length > 0 && (
        <div className="rounded-md border bg-muted/30 p-3 space-y-1.5">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold">
              Filtered by frequency cap ({cappedOut.length})
            </p>
            <Badge variant="outline" className="text-[10px]">cap {frequencyCap}/24h</Badge>
          </div>
          <ul className="space-y-1">
            {cappedOut.map(({ campaign, impressions, reason }) => (
              <li key={campaign.id} className="text-[11px] text-muted-foreground flex items-center justify-between gap-2">
                <span className="truncate">{campaign.title} <span className="opacity-60">· {campaign.advertiser_name}</span></span>
                <span className="shrink-0 tabular-nums">
                  {impressions}/{frequencyCap} · {reason}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-[10px] text-muted-foreground italic">
            Caps are stored per-browser session. Use the &quot;reset&quot; helper in dev tools to clear.
          </p>
        </div>
      )}
    </div>
  );
}

export default BannerSlot;
